import Image from 'next/image';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/src/components/ui/dropdown-menu';
import { IResolvedHeaderLocale } from '@/src/utils/collections/header.mapper';

type IHeaderLocalesProps = {
  locale: string;
  localeList: { [key: string]: IResolvedHeaderLocale };
  buttonLanguage: string | null;
  setOpen?: Dispatch<SetStateAction<boolean>>;
};

export const HeaderLocales = ({
  locale,
  localeList,
  buttonLanguage,
  setOpen,
}: IHeaderLocalesProps) => {
  const pathname = usePathname();
  const currentPathWithoutLocale = pathname
    .split('/')
    .filter(item => item != '')
    .splice(1)
    .join('/');
  const currentLocale = useMemo(() => localeList[locale], [locale, localeList]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className={'cursor-pointer'}>
        {currentLocale && (
          <div
            className={
              'mr-3 mt-8 flex w-full flex-row items-center border-y border-white py-3 text-xl font-bold text-white lg:mt-0 lg:border-none lg:py-0'
            }
          >
            <p className={'flex-1 lg:hidden'}>{buttonLanguage}</p>
            {currentLocale.icon && (
              <Image
                src={currentLocale.icon.icon}
                alt={currentLocale.displayText}
                width={24}
                height={24}
              />
            )}
            <p className={'ml-2 text-xs text-white'}>
              {currentLocale.locale?.toUpperCase()}
            </p>
          </div>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={'w-[calc(100vw-2.5rem)] lg:w-auto lg:p-0'}
      >
        {Object.entries(localeList).map(([key, item]) => (
          <DropdownMenuItem key={`dropdown-content-${key}`} asChild>
            <Link
              className={'cursor-pointer no-underline'}
              href={`/${key}/${currentPathWithoutLocale}`}
              onClick={() => {
                if (setOpen) setOpen(false);
                window.dataLayer?.push({
                  event: 'click_button',
                  click_id: `/${key}/${currentPathWithoutLocale}`,
                  click_label: item.displayText,
                });
              }}
            >
              {item && (
                <Image
                  src={item.icon.icon}
                  alt={item.displayText}
                  width={24}
                  height={24}
                />
              )}
              {item.displayText}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
